import React, { memo, useEffect, useMemo, useCallback } from 'react'
import { Card, Checkbox, Form, Switch, Tooltip } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import { extract } from '../../../../../_utils/object'
import { canShowField } from '../helpers'
import { INotificationFieldSuggestions } from '../../../../../interfaces/notification-field-suggestions'
import { SuggestedFieldsForm, VariantCardProps } from '../types'

/**
 * VariantCardAlt component
 *   Card view for AI-generated variants with field selection and add functionality
 */
export const VariantCardAlt = memo(
    ({
        variant,
        variantIndex,
        domain,
        canAddVariant,
        isSelectedForReplace,
        isSelectedForAdd,
        onReplaceSelectionChange,
        onAddSelectionChange,
        hasOtherReplaceSelection = false, // New prop to indicate if another card is selected for replace
    }: VariantCardProps) => {
        const [form] = useForm<SuggestedFieldsForm>()

        const cardSuggestedImage = useMemo<string | null>(() => {
            if (!variant?.imageUrl && !variant?.compositeImage) return null
            if (variant?.compositeImage) return extract(variant.compositeImage, 'url')
            return variant.imageUrl
        }, [variant?.imageUrl, variant?.compositeImage])

        const hasTitleSuggestion = !!variant?.title
        const hasBodySuggestion = !!variant?.body
        const hasImageSuggestion = cardSuggestedImage !== null
        const hasTone = !!variant?.tone
        const hasReason = !!variant?.reason

        // Initialize form with default selections
        useEffect(() => {
            form.setFieldsValue({
                title: hasTitleSuggestion,
                body: hasBodySuggestion,
                imageUrl: hasImageSuggestion,
            })
        }, [form, hasTitleSuggestion, hasBodySuggestion, hasImageSuggestion])

        const getSelections = (): Partial<INotificationFieldSuggestions> => {
            const values = form.getFieldsValue()
            let selections: Partial<INotificationFieldSuggestions> = {}

            if (values.title && canShowField(domain, 'title')) {
                selections.title = variant.title
            }
            if (values.body && canShowField(domain, 'body')) {
                selections.body = variant.body
            }
            if (values.imageUrl && canShowField(domain, 'imageUrl')) {
                if (variant.compositeImage) {
                    selections.compositeImage = variant.compositeImage
                    selections.imageUrl = variant.imageUrl
                } else {
                    selections.imageUrl = cardSuggestedImage
                }
            }

            return selections
        }

        // Get all available selections for adding as new variant (includes all AI-generated content)
        const getAllSelections = (): Partial<INotificationFieldSuggestions> => {
            let selections: Partial<INotificationFieldSuggestions> = {}

            // Always include title if available (required for variants)
            if (hasTitleSuggestion && canShowField(domain, 'title')) {
                selections.title = variant.title
            }
            // Always include body if available
            if (hasBodySuggestion && canShowField(domain, 'body')) {
                selections.body = variant.body
            }
            // Include image if available
            if (hasImageSuggestion && canShowField(domain, 'imageUrl')) {
                if (variant.compositeImage) {
                    selections.compositeImage = variant.compositeImage
                    selections.imageUrl = variant.imageUrl
                } else {
                    selections.imageUrl = cardSuggestedImage
                }
            }

            return selections
        }

        const handleReplaceSelectionChange = useCallback(
            (selected: boolean) => {
                const selections = getSelections() // Use form selections for replace (respects user choices)
                onReplaceSelectionChange(variantIndex, selected, selections)
            },
            [variantIndex, onReplaceSelectionChange, getSelections],
        )

        const handleAddSelectionChange = useCallback(
            (selected: boolean) => {
                const selections = getAllSelections() // Use all available content for add (complete variant)
                onAddSelectionChange(variantIndex, selected, selections)
            },
            [variantIndex, onAddSelectionChange, getAllSelections],
        )

        // Click handlers for field content
        const handleTitleClick = useCallback(() => {
            const currentValue = form.getFieldValue('title')
            form.setFieldsValue({ title: !currentValue })
        }, [form])

        const handleBodyClick = useCallback(() => {
            const currentValue = form.getFieldValue('body')
            form.setFieldsValue({ body: !currentValue })
        }, [form])

        const handleImageClick = useCallback(() => {
            const currentValue = form.getFieldValue('imageUrl')
            form.setFieldsValue({ imageUrl: !currentValue })
        }, [form])

        const cardTitle = (
            <div className="variant-card-title">
                <span>Variant {variantIndex + 1}</span>
                {hasTone && <span className="variant-tone">{variant.tone}</span>}
            </div>
        )

        const cardActions = [
            <div key="replace" className="selection-action">
                <Tooltip
                    title={hasOtherReplaceSelection && !isSelectedForReplace ? 'Replace current already selected' : ''}
                    placement="top"
                >
                    <span className="selection-action-wrapper">
                        <Switch
                            className="replace-switch"
                            checked={isSelectedForReplace}
                            disabled={isSelectedForAdd || (hasOtherReplaceSelection && !isSelectedForReplace)} // Disable if selected for add or another card is selected for replace
                            onChange={handleReplaceSelectionChange}
                            size="small"
                        />
                    </span>
                </Tooltip>
                <span className="selection-label">Replace current</span>
            </div>,
            <div key="add" className="selection-action">
                <Tooltip
                    title={!canAddVariant && !isSelectedForAdd ? 'Notification variants max reached' : ''}
                    placement="top"
                >
                    <span className="selection-action-wrapper">
                        <Switch
                            className="add-switch"
                            checked={isSelectedForAdd}
                            disabled={(!canAddVariant && !isSelectedForAdd) || isSelectedForReplace} // Disable if selected for replace or at limit
                            onChange={handleAddSelectionChange}
                            size="small"
                        />
                    </span>
                </Tooltip>
                <span className="selection-label">Add as new</span>
            </div>,
        ]

        return (
            <Card
                className={`variant-card ${isSelectedForReplace ? 'selected-for-replace' : ''} ${
                    isSelectedForAdd ? 'selected-for-add' : ''
                }`.trim()}
                title={cardTitle}
                actions={cardActions}
                size="small"
                style={{ height: '100%' }}
            >
                <Form form={form} size="small">
                    {hasReason && (
                        <div className="field-section reason-section">
                            <div className="reason-label">Why this variant:</div>
                            <div className="reason-text">{variant.reason}</div>
                        </div>
                    )}

                    {hasTitleSuggestion && canShowField(domain, 'title') && (
                        <div className="field-section">
                            <Form.Item name="title" valuePropName="checked">
                                <Checkbox>Title</Checkbox>
                            </Form.Item>
                            <div className="field-preview content-clickable" onClick={handleTitleClick}>
                                {variant.title}
                            </div>
                        </div>
                    )}

                    {hasBodySuggestion && canShowField(domain, 'body') && (
                        <div className="field-section">
                            <Form.Item name="body" valuePropName="checked">
                                <Checkbox>Body</Checkbox>
                            </Form.Item>
                            <div className="field-preview content-clickable" onClick={handleBodyClick}>
                                {variant.body}
                            </div>
                        </div>
                    )}

                    {hasImageSuggestion && canShowField(domain, 'imageUrl') && (
                        <div className="field-section">
                            <Form.Item name="imageUrl" valuePropName="checked">
                                <Checkbox>Image</Checkbox>
                            </Form.Item>
                            <div className="field-preview image-preview content-clickable" onClick={handleImageClick}>
                                <div
                                    className="suggested-image-small"
                                    style={{ backgroundImage: `url('${cardSuggestedImage}')` }}
                                />
                            </div>
                        </div>
                    )}
                </Form>
            </Card>
        )
    },
)

VariantCardAlt.displayName = 'VariantCardAlt'
